/**
 * Section helpers for Daily Schedule v2. All entries are fetched from
 * api-daily-schedule-v2-service — this file holds the enums the UI needs
 * (sleep-quality bands) plus the time-format helpers shared by the table
 * cell, the modal, and the sleep-derivation logic in the hydration card.
 */

// Sleep quality bands — matches the segmented pill on the hydration/sleep
// card. `key` maps 1:1 to the backend enum (constants.SleepQuality*).
export const SLEEP_QUALITY_OPTIONS = [
  { key: "good", label: "Good", bg: "#e8f7ef", fg: "#1b7f4c" },
  { key: "fair", label: "Fair", bg: "#fdf1e2", fg: "#b45309" },
  { key: "poor", label: "Poor", bg: "#fdeaea", fg: "#b91c1c" },
];

// Time-format helpers. TimeMinutes is minutes-past-midnight so ordering is
// trivial and time-zone free. UI formats to 12-hour "HH:MM am/pm" per the
// mock.

// "H:MM am/pm" — used in the table cell.
export const formatTimeAmPm = (mins) => {
  if (mins == null || mins < 0 || mins >= 1440) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const ap = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12 < 10 ? "0" : ""}${h12}:${m < 10 ? "0" : ""}${m} ${ap}`;
};

// "HH:MM" 24-hour string for <input type="time">.
export const minutesToTimeInput = (mins) => {
  if (mins == null || mins < 0 || mins >= 1440) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h < 10 ? "0" : ""}${h}:${m < 10 ? "0" : ""}${m}`;
};

// Parse "HH:MM" back to minutes-past-midnight. Returns null on invalid.
export const timeInputToMinutes = (str) => {
  if (!str || typeof str !== "string") return null;
  const parts = str.split(":");
  if (parts.length !== 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
};

// Whitespace-normalize an activity name — mirrors
// transformer.NormalizeActivity on the backend so the FE dedup check sees
// the same key the server uses.
export const normalizeActivity = (a) => (a || "").trim().split(/\s+/).join(" ");

// Well-known activity labels the Sleep card auto-derives from. Case-
// insensitive match against normalized activity name.
export const SLEEP_ENTRY_NAME = "sleep";
export const WAKE_ENTRY_NAME = "wake up";

/**
 * Derive sleep-time / wake-time / duration from the timeline. Returns
 *   { sleepMins?, wakeMins?, durationMins? }
 * with fields present only when the matching entry exists. Duration wraps
 * over midnight (wakeMins next-day when sleepMins > wakeMins).
 */
export const deriveSleep = (entries) => {
  const out = {};
  const sleep = (entries || []).find(
    (e) => normalizeActivity(e.activity).toLowerCase() === SLEEP_ENTRY_NAME
  );
  const wake = (entries || []).find(
    (e) => normalizeActivity(e.activity).toLowerCase() === WAKE_ENTRY_NAME
  );
  if (sleep) out.sleepMins = sleep.timeMinutes;
  if (wake) out.wakeMins = wake.timeMinutes;
  if (out.sleepMins != null && out.wakeMins != null) {
    let d = out.wakeMins - out.sleepMins;
    if (d <= 0) d += 1440; // slept through midnight
    out.durationMins = d;
  }
  return out;
};

// "Xh Ym" from a minutes count — used for the Sleep card's auto-duration.
export const formatDuration = (mins) => {
  if (mins == null || mins < 0) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

// ── Calendar-day helpers ────────────────────────────────────────────────
//
// The store is keyed by "YYYY-MM-DD" in the CLINICIAN'S local day (the
// browser knows it; the UTC server does not), so all of these work on
// local-time Date objects and never go through toISOString().

const pad2 = (n) => (n < 10 ? "0" : "") + n;

// Local Date → "YYYY-MM-DD".
export const toDateKey = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// "YYYY-MM-DD" → local Date at midnight (null on malformed input).
export const fromDateKey = (key) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key || "")) return null;
  const [y, m, d] = key.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  // Reject roll-over like 2026-02-30 → Mar 2.
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt;
};

export const todayDate = () => toDateKey(new Date());

// Shift a "YYYY-MM-DD" by N days (negative for earlier).
export const shiftDateKey = (key, days) => {
  const dt = fromDateKey(key);
  if (!dt) return key;
  dt.setDate(dt.getDate() + days);
  return toDateKey(dt);
};

// "Wed, 17 Sep 2026" — the date bar and the filled-days list.
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const formatDateKey = (key) => {
  const dt = fromDateKey(key);
  if (!dt) return key || "—";
  return `${DAY_NAMES[dt.getDay()]}, ${dt.getDate()} ${MONTH_NAMES[dt.getMonth()]} ${dt.getFullYear()}`;
};

// "12 Sep 2026" — the builder banner ("Adding schedule for 12 Sep 2026").
export const formatDateDMY = (key) => {
  const dt = fromDateKey(key);
  if (!dt) return key || "—";
  return `${dt.getDate()} ${MONTH_NAMES[dt.getMonth()]} ${dt.getFullYear()}`;
};

// "Today" / "Yesterday" / formatted date — relative label for the list.
export const relativeDayLabel = (key) => {
  const today = todayDate();
  if (key === today) return "Today";
  if (key === shiftDateKey(today, -1)) return "Yesterday";
  return formatDateKey(key);
};

// Water intake — 300 ml per glass, the same glass the patient app logs in.
// Glasses (not litres) are what's stored and synced with the water-intake
// vital, so this only changes the litre read-out.
export const ML_PER_GLASS = 300;
export const glassesToLiters = (glasses) => {
  if (!glasses || glasses < 0) return 0;
  return Math.round((glasses * ML_PER_GLASS) / 100) / 10; // 1 decimal
};
